import Skeleton from '@mui/material/Skeleton';
import { CircleCheck, RefreshCw, TriangleAlert } from 'lucide-react';
import './EscalatedStaleTicketsPanel.scss';

export default function EscalatedStaleTicketsPanel({ tickets, loading, error, staleDays, onRetry, onTicketClick }) {
    const className = 'escalatedStaleTicketsPanel';

    const daysIdle = (modifiedTimeStamp) => {
        if (!modifiedTimeStamp) return null;
        // modifiedTimeStamp is milliseconds since epoch (confirmed against live data during
        // planning), matching Date.now() — no unit conversion needed.
        const millis = Date.now() - modifiedTimeStamp;
        return Math.floor(millis / (24 * 60 * 60 * 1000));
    };

    if (loading) {
        return (
            <div className={className}>
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={className + '__skeletonRow'}>
                        <Skeleton variant="text" width={72} height={16} />
                        <Skeleton variant="text" width="100%" height={16} style={{ flex: 1 }} />
                        <Skeleton variant="rounded" width={72} height={20} />
                        <Skeleton variant="rounded" width={64} height={20} />
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className={className + '__state'}>
                <TriangleAlert className={className + '__state__icon'} />
                <p className={className + '__state__text'}>Couldn't load escalated or stale tickets.</p>
                {onRetry && (
                    <button type="button" className={className + '__state__retry'} onClick={onRetry}>
                        <RefreshCw />
                        Try again
                    </button>
                )}
            </div>
        );
    }

    if (!tickets || tickets.length === 0) {
        return (
            <div className={className + '__state'}>
                <CircleCheck className={className + '__state__icon ' + className + '__state__icon--ok'} />
                <p className={className + '__state__text'}>
                    Nothing escalated or stale — every ticket's had activity in the last {staleDays} days.
                </p>
            </div>
        );
    }

    return (
        <div className={className}>
            {tickets.map((ticket) => {
                const idle = daysIdle(ticket.modifiedTimeStamp);
                return (
                    <div
                        key={ticket.id}
                        className={className + '__row'}
                        role="button"
                        tabIndex={0}
                        onClick={() => onTicketClick?.(ticket)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onTicketClick?.(ticket);
                            }
                        }}
                    >
                        <div className={className + '__row__ref'}>{ticket.ticketId}</div>
                        <div className={className + '__row__title'} title={ticket.ticketTitle}>
                            {ticket.ticketTitle}
                        </div>
                        <div className={className + '__row__badges'}>
                            {ticket.isEscalated && (
                                <span className={className + '__row__badges__escalated'}>Escalated</span>
                            )}
                            {ticket.isStale && idle !== null && (
                                <span className={className + '__row__badges__stale'}>{idle}d idle</span>
                            )}
                        </div>
                        <div
                            className={className + '__row__status'}
                            style={{ '--status-color': ticket.statusColor || 'var(--dbp-text-muted)' }}
                        >
                            <span className={className + '__row__status__dot'} />
                            {ticket.statusTitle}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
